import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import Logo from '../Assets/magi.png';
import { useTaxPortal } from '../utils/useTaxPortal';
import { getApiUrl, isAdminRole } from '../authConfig';
import { getValidToken } from '../utils/msalTokenUtil';
import TaxPortalLayout from '../components/Common/TaxPortalLayout';
import AddressAutocomplete from '../components/Common/AddressAutocomplete';
import {
  FaMoneyBillWave, FaClock, FaEye, FaEnvelope, FaLaptopCode, FaHandshake,
  FaPlus, FaPrint, FaArrowLeft, FaFileExcel,
  FaChevronLeft, FaChevronRight, FaEllipsisV, FaInbox,
  FaFileInvoiceDollar, FaSearch, FaFilter, FaCheckCircle, FaBan, FaList, FaTimes
} from 'react-icons/fa';

const currentYear = new Date().getFullYear();
const yearOptions = Array.from({ length: 5 }, (_, i) => (currentYear - i).toString());

const WORK_ITEMS = {
  WebAndDigitalMarketing: [
    "Marketing Website Development / Revamp: Complete design, development, deployment, or overhaul of the primary marketing website.",
    "Digital Marketing Services: Comprehensive online marketing campaigns, including SEO optimization, social media management, and digital advertising."
  ],
  SubscriptionsAndAPIs: [
    "OrientalPay Service Subscription (Yearly): Annual subscription fee and licensing for OrientalPay services.",
    "Azure Communication Services (Monthly): Monthly subscription and usage fees for Azure cloud communication and messaging APIs.",
    "Telnyx Fax Service (Monthly): Monthly subscription for programmable cloud faxing services.",
    "BankStmt PDF to Excel AI API (Monthly): Monthly subscription for AI-powered bank statement data extraction and conversion API."
  ],
  InfrastructureAndEmail: [
    "Microsoft Email Setup: Initial configuration, domain verification, data migration, and user account provisioning for enterprise email.",
    "Microsoft Email Subscription (Yearly): Annual licensing and subscription fees for Microsoft enterprise email and productivity tools.",
    "Cloud Server Hosting & Storage: Routine monthly hosting fees for dedicated cloud servers and secure data storage."
  ],
  ITSupportAndMaintenance: [
    "OrientalPay Service Support: Ongoing technical troubleshooting, API integration assistance, and general maintenance for OrientalPay.",
    "General IT Helpdesk Support: Routine remote troubleshooting, software updates, access management, and user workstation assistance.",
    "Server Maintenance & Monitoring: Proactive monthly monitoring, patch management, and automated backup verification for active servers.",
    "Network Security & Firewall Management: Routine configuration, monitoring, and updating of network security protocols to ensure data integrity."
  ],
  StaffingAndManagedServices: [
    "Resource Staffing Payroll Cost (Manjula G) (Monthly): Dedicated resource allocation and payroll cost for Business Assistance."
  ]
};

// Avatar helper
const AVATAR_COLORS = [
  { bg: '#dbeafe', text: '#1e40af' }, { bg: '#fce7f3', text: '#9d174d' },
  { bg: '#d1fae5', text: '#065f46' }, { bg: '#ede9fe', text: '#5b21b6' },
  { bg: '#fef3c7', text: '#92400e' }, { bg: '#cffafe', text: '#164e63' },
  { bg: '#fee2e2', text: '#991b1b' }, { bg: '#e0f2fe', text: '#0c4a6e' },
];
const getAvatarColor = (name = '') => {
  const code = (name.charCodeAt(0) || 0) + (name.charCodeAt(1) || 0);
  return AVATAR_COLORS[code % AVATAR_COLORS.length];
};
const getInitials = (name = '') => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return (parts[0] || 'I').substring(0, 2).toUpperCase();
};

const ClientAvatar = ({ name }) => {
  const c = getAvatarColor(name);
  return (
    <span className="name-avatar flex-shrink-0 d-none d-sm-flex" style={{ width: 32, height: 32, backgroundColor: c.bg, color: c.text, fontSize: '11px' }} aria-hidden="true">
      {getInitials(name)}
    </span>
  );
};

const SkeletonRows = ({ count = 7 }) => (
  <>
    {Array.from({ length: count }).map((_, i) => (
      <tr key={i} className="skeleton-row">
        <td className="px-3"><span className="skeleton-cell d-block" style={{ width: 60, height: 16 }} /></td>
        <td><span className="skeleton-cell d-block" style={{ width: 80, height: 16 }} /></td>
        <td>
          <div className="d-flex align-items-center gap-2">
            <span className="skeleton-cell d-none d-sm-block" style={{ width: 32, height: 32, borderRadius: '50%' }} />
            <span className="skeleton-cell d-block" style={{ width: 120 + (i % 3) * 30, height: 16 }} />
          </div>
        </td>
        <td><span className="skeleton-cell d-block" style={{ width: 60, height: 16 }} /></td>
        <td><span className="skeleton-cell d-block" style={{ width: 60, height: 16 }} /></td>
        <td><span className="skeleton-cell d-block" style={{ width: 60, height: 16 }} /></td>
        <td><span className="skeleton-cell d-block" style={{ width: 60, height: 16 }} /></td>
        <td><span className="skeleton-cell d-block" style={{ width: 80, height: 16 }} /></td>
        <td><span className="skeleton-cell d-block" style={{ width: 90, height: 26, borderRadius: 99 }} /></td>
        <td className="px-4"><span className="skeleton-cell d-block ms-auto" style={{ width: 60, height: 26, borderRadius: 99 }} /></td>
      </tr>
    ))}
  </>
);

const InvoicePage = () => {
  const portalState = useTaxPortal();
  const location = useLocation();
  const { account, msalInstance, isInitialized } = portalState;

  const [isLoading, setIsLoading] = useState(true);
  const [invoiceId, setInvoiceId] = useState(null);
  const [savedInvoices, setSavedInvoices] = useState([]);
  const [invoiceNum, setInvoiceNum] = useState('');
  const [invoiceDate, setInvoiceDate] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [billToName, setBillToName] = useState('');
  const [billToEmail, setBillToEmail] = useState('');
  const [billToAddress, setBillToAddress] = useState('');
  const [sourceRecord, setSourceRecord] = useState(null);
  const [shouldTriggerPrint, setShouldTriggerPrint] = useState(false);
  const [pendingEmailAction, setPendingEmailAction] = useState(false);

  const [viewMode, setViewMode] = useState(() => {
    const state = location.state;
    if (state && state.customer) return 'form';
    return 'dashboard';
  });

  const [items, setItems] = useState(() => {
    const state = location.state;
    if (state && state.customer) {
      const defaultDesc = WORK_ITEMS.WebAndDigitalMarketing[0];
      const parts = defaultDesc.split(':');
      return [{
        id: Date.now(),
        itemName: parts[0].trim(),
        itemDesc: parts.slice(1).join(':').trim(),
        period: "yearly",
        startDate: "",
        endDate: "",
        amount: "",
        hst: true
      }];
    }
    // Add hst: true for new blank invoices as well
    return [{ id: Date.now(), itemName: "", itemDesc: "", period: "monthly", startDate: "", endDate: "", amount: "", hst: true }];
  });

  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('Due');
  const [yearFilter, setYearFilter] = useState(currentYear.toString());
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const filterRef = useRef(null);
  const [pageIndex, setPageIndex] = useState(0);
  const pageSize = 10;
  const originalTitleRef = useRef(document.title);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target)) {
        setIsFilterOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isAdmin = isAdminRole(account?.username);

  useEffect(() => {
    if (account && !isAdmin && viewMode === 'dashboard') {
      setViewMode('form');
    }
  }, [account, isAdmin, viewMode]);

  const { paidTotal, dueTotal, paidTaxTotal, serviceTotal, supportTotal, paidCount, dueCount, cancelledCount, totalCount } = useMemo(() => {
    let paidTotal = 0;
    let dueTotal = 0;
    let paidTaxTotal = 0;
    let serviceTotal = 0;
    let supportTotal = 0;
    let paidCount = 0;
    let dueCount = 0;
    let cancelledCount = 0;
    let totalCount = 0;

    savedInvoices.forEach(inv => {
      const total = parseFloat(inv.total) || 0;
      const tax = parseFloat(inv.tax) || 0;
      const status = inv.status || 'Due';

      if (status === 'Paid') {
        paidTotal += total;
        paidTaxTotal += tax;
        paidCount += 1;
      } else if (status === 'Due') {
        dueTotal += total;
        dueCount += 1;
      } else if (status === 'Cancelled') {
        cancelledCount += 1;
      }
      totalCount += 1;

      let parsedItems = [];
      if (typeof inv.items === 'string') {
        try { parsedItems = JSON.parse(inv.items); } catch(e){}
      } else if (Array.isArray(inv.items)) {
        parsedItems = inv.items;
      }

      parsedItems.forEach(item => {
        const amt = parseFloat(item.amount?.toString().replace(/[^0-9.-]/g, '')) || 0;
        const itemName = item.itemName || (item.description ? item.description.split(':')[0].trim() : '');
        const nameLower = itemName.toLowerCase();

        const supportAndMaintenanceItems = WORK_ITEMS.ITSupportAndMaintenance.map(wi => wi.split(':')[0].trim().toLowerCase());
        const isSupport = supportAndMaintenanceItems.includes(nameLower) || nameLower.includes('support');

        if (isSupport) {
          supportTotal += amt;
        } else {
          serviceTotal += amt;
        }
      });
    });

    return { paidTotal, dueTotal, paidTaxTotal, serviceTotal, supportTotal, paidCount, dueCount, cancelledCount, totalCount };
  }, [savedInvoices]);

  useEffect(() => {
    const today = new Date();
    const due = new Date(today);
    due.setDate(today.getDate() + 2);
    const formatDate = date => date.toISOString().slice(0, 10);

    if (!invoiceId) {
      setInvoiceDate(formatDate(today));
      setDueDate(formatDate(due));
      
      if (location.state && location.state.customer) {
        const cust = location.state.customer;
        setBillToName(cust.businessName || cust.name || '');
        setBillToEmail(cust.email || '');
        setBillToAddress(cust.address || '');
        setSourceRecord({ id: cust.id || cust._id, type: location.state.taxType });
      }
    }

    const fetchNextInvoiceNumber = async () => {
      if (!account || !isInitialized) return;
      try {
        const token = await getValidToken(msalInstance, account);
        const response = await fetch(getApiUrl('/mtsapi/invoices/next-number'), { headers: { Authorization: `Bearer ${token}` } });
        if (response.ok) {
          const data = await response.json();
          setInvoiceNum(data.invoice_num);
        }
      } catch (err) { console.error(err); toast.error("Failed to fetch next invoice number."); }
    };

    const fetchSavedInvoices = async () => {
      if (!account || !isInitialized) return;
      setIsLoading(true);
      try {
        const token = await getValidToken(msalInstance, account);
        const response = await fetch(getApiUrl('/mtsapi/invoices'), { headers: { Authorization: `Bearer ${token}` } });
        if (response.ok) {
          setSavedInvoices(await response.json());
        }
      } catch (err) { console.error(err); toast.error("Failed to fetch saved invoices."); }
      finally { setIsLoading(false); }
    };

    if (!invoiceId && viewMode === 'form') fetchNextInvoiceNumber();
    if (viewMode === 'dashboard') fetchSavedInvoices();
  }, [account, isInitialized, msalInstance, invoiceId, location.state, viewMode, setViewMode]);

  const { subtotal, tax, total } = useMemo(() => {
    const currentSubtotal = items.reduce((sum, item) => {
      const val = parseFloat(item.amount?.toString().replace(/[^0-9.]/g, '')) || 0;
      return sum + val;
    }, 0);
    const taxableSubtotal = items.reduce((sum, item) => {
      const val = parseFloat(item.amount?.toString().replace(/[^0-9.]/g, '')) || 0;
      return item.hst ? sum + val : sum;
    }, 0);
    const currentTax = taxableSubtotal * 0.13;
    return { subtotal: currentSubtotal, tax: currentTax, total: currentSubtotal + currentTax };
  }, [items]);

  useEffect(() => {
    if (shouldTriggerPrint) {
      setShouldTriggerPrint(false);
      window.print();
      if (pendingEmailAction) {
        setPendingEmailAction(false);
        portalState.setEmailModalConfig({
          customer: { email: billToEmail, name: billToName },
          action: 'sendInvoice',
          taxType: sourceRecord ? sourceRecord.type : 'Personal',
          customData: {
            invoiceNum,
            dueDate,
            totalAmount: total.toFixed(2)
          }
        });
      }
    }
  }, [shouldTriggerPrint, pendingEmailAction, portalState, billToEmail, billToName, invoiceNum, dueDate, total, sourceRecord]);

  useEffect(() => {
    const textareas = document.querySelectorAll('.invoice-box textarea');
    textareas.forEach(ta => {
      ta.style.height = 'auto';
      ta.style.height = `${ta.scrollHeight}px`;
    });
  }, [items, viewMode]);

      const sanitizeTitleSegment = (value, fallback) => {
      const normalized = (value ?? '')
        .toString()
        .trim()
        .replace(/[^A-Za-z0-9#_-]+/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_+|_+$/g, '');

      return normalized || fallback;
    };
    
  useEffect(() => {


    if (viewMode === 'form') {
      const safeInvoiceNum = sanitizeTitleSegment(invoiceNum, 'invoice');
      const safeBillTo = sanitizeTitleSegment(billToName, 'client');
      const safeInvoiceDate = sanitizeTitleSegment(invoiceDate, 'date');
      document.title = `invoice_#${safeInvoiceNum}_${safeBillTo}_${safeInvoiceDate}`;
    }
  }, [invoiceNum, billToName, invoiceDate, viewMode]);

  useEffect(() => {
    const handleBeforePrint = () => {
      if (viewMode === 'form') {
        const safeInvoiceNum = sanitizeTitleSegment(invoiceNum, 'invoice');
        const safeBillTo = sanitizeTitleSegment(billToName, 'client');
        const safeInvoiceDate = sanitizeTitleSegment(invoiceDate, 'date');
        document.title = `invoice_#${safeInvoiceNum}_${safeBillTo}_${safeInvoiceDate}`;
      }
    };
    const handleAfterPrint = () => { document.title = originalTitleRef.current; };

    window.addEventListener('beforeprint', handleBeforePrint);
    window.addEventListener('afterprint', handleAfterPrint);
    return () => {
      window.removeEventListener('beforeprint', handleBeforePrint);
      window.removeEventListener('afterprint', handleAfterPrint);
    };
  }, [invoiceNum, billToName, invoiceDate, viewMode]);

  const handleAddRow = () => {
    setItems(prev => [...prev, { id: Date.now(), itemName: "", itemDesc: "", period: "yearly", startDate: "", endDate: "", amount: "", hst: true }]);
  };

  const handleRemoveRow = (id) => {
    if (items.length > 1) {
      setItems(prev => prev.filter(item => item.id !== id));
    }
  };

  const handleItemChange = (id, field, value) => {
    setItems(prev => prev.map(item => item.id === id ? { ...item, [field]: value } : item));
  };

  const handleTextareaInput = (e) => {
    const element = e.target;
    element.style.height = 'auto';
    element.style.height = `${element.scrollHeight}px`;
  };

  const handleStatusChange = async (invoice, newStatus) => {
    try {
      const token = await getValidToken(msalInstance, account);
      
      const updatePayload = { ...invoice, status: newStatus };

      const response = await fetch(getApiUrl(`/mtsapi/invoices/${invoice.id}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(updatePayload)
      });

      if (response.ok) {
        setSavedInvoices(prev => prev.map(inv => inv.id === invoice.id ? { ...inv, status: newStatus } : inv));
        
        if (newStatus !== 'Cancelled') {
           await fetch(getApiUrl(`/mtsapi/invoices/${invoice.id}/sync-status`), {
             method: 'POST',
             headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
             body: JSON.stringify({ status: newStatus, invoice_num: invoice.invoice_num })
           });
        }
      } else {
        toast.error('Failed to update invoice status.');
      }
    } catch (err) {
      console.error(err);
      toast.error('Error updating invoice status.');
    }
  };

  const handleViewInvoice = (inv) => {
    setInvoiceId(inv.id);
    setInvoiceNum(inv.invoice_num);
    setInvoiceDate(inv.invoice_date);
    setDueDate(inv.due_date);
    try {
      const parsedBillTo = JSON.parse(inv.bill_to_id);
      setBillToName(parsedBillTo.name || '');
      setBillToEmail(parsedBillTo.email || '');
      setBillToAddress(parsedBillTo.address || '');
    } catch (e) {
      setBillToName(inv.bill_to_id || '');
      setBillToEmail('');
      setBillToAddress('');
    }
    let loadedItems = typeof inv.items === 'string' ? JSON.parse(inv.items) : inv.items;
    loadedItems = loadedItems.map(item => {
      const name = item.itemName !== undefined ? item.itemName : (item.description ? item.description.split(':')[0].trim() : '');
      const desc = item.itemDesc !== undefined ? item.itemDesc : (item.description && item.description.includes(':') ? item.description.split(':').slice(1).join(':').trim() : '');
      return { ...item, itemName: name, itemDesc: desc };
    });
    setItems(loadedItems);
    setViewMode('form');
  };

  const handleEmailInvoice = (inv) => {
    let targetEmail = '';
    let targetName = '';
    try {
      const parsed = JSON.parse(inv.bill_to_id);
      targetEmail = parsed.email || '';
      targetName = parsed.name || '';
      // Address is not used here but good practice to be aware of it.
    } catch (e) {}
    portalState.setEmailModalConfig({
      customer: { email: targetEmail, name: targetName },
      action: 'sendInvoice',
      taxType: 'Personal',
      customData: {
        invoiceNum: inv.invoice_num,
        dueDate: inv.due_date,
        totalAmount: (inv.total || 0).toFixed(2)
      }
    });
  };

  const handleSaveInvoice = async (shouldPrint = false, shouldEmail = false) => {
    if (!account) {
      toast.error('You must be logged in to save invoices.');
      return;
    }
    

    try {
      const token = await getValidToken(msalInstance, account);
      const payload = {
        invoice_num: invoiceNum,
        invoice_date: invoiceDate,
        due_date: dueDate,
        bill_to_id: JSON.stringify({ name: billToName, email: billToEmail, address: billToAddress }),
        items: items.map(item => ({
          ...item,
          description: item.itemDesc ? `${item.itemName}: ${item.itemDesc}` : (item.itemName || '')
        })),
        subtotal,
        tax,
        total,
        status: invoiceId ? (savedInvoices.find(i => i.id === invoiceId)?.status || 'Due') : 'Due'
      };

      let url = getApiUrl('/mtsapi/invoices');
      let method = 'POST';

      if (invoiceId) {
        url = getApiUrl(`/mtsapi/invoices/${invoiceId}`);
        method = 'PUT';
      }

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const data = await response.json();
        const newInvoiceId = invoiceId || data.id;
        const newInvoiceNum = data.invoice_num || invoiceNum;
        
        if (!invoiceId && data.id) {
          setInvoiceId(newInvoiceId);
          setInvoiceNum(newInvoiceNum);
        }

        if (sourceRecord) {
          try {
            const updateUrl = getApiUrl(`/mtsapi/corporate/${sourceRecord.id}`);
            const updatePayload = {
              invoiceNo: newInvoiceNum.toString(),
              invoiceAmount: total.toFixed(2),
              status: 'Payment Pending',
              updatedBy: account?.username || account?.name || 'System'
            };
            
            updatePayload.paymentStatus = 'Unpaid';
            
            await fetch(updateUrl, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
              body: JSON.stringify(updatePayload)
            });
          } catch (err) { console.error(err); toast.error("Failed to sync invoice status to the source record."); }
        }
        
        toast.success('Invoice saved successfully!');
        if (shouldEmail) setPendingEmailAction(true);
        if (shouldPrint) setShouldTriggerPrint(true);
        
        const res = await fetch(getApiUrl('/mtsapi/invoices'), { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) setSavedInvoices(await res.json());
      } else {
        toast.error('Failed to save invoice.');
      }
    } catch (err) {
      console.error(err);
      toast.error('Error saving invoice.');
    }
  };

  const filteredInvoices = savedInvoices.filter(inv => {
    const status = inv.status || 'Due';
    if (statusFilter !== 'All' && status !== statusFilter) {
      return false;
    }

    if (yearFilter && inv.invoice_date) {
      const invoiceYear = new Date(inv.invoice_date).getFullYear().toString();
      if (invoiceYear !== yearFilter) {
        return false;
      }
    }
    const searchLower = searchText.toLowerCase();
    let billTo = 'Unknown';
    try {
      const parsed = JSON.parse(inv.bill_to_id);
      billTo = (parsed.name || inv.bill_to_id).toLowerCase();
      // Note: address is not part of search, which is probably fine.
    } catch (e) { billTo = String(inv.bill_to_id || '').toLowerCase(); }
    
    let service = 'Unknown';
    try {
      const parsedItems = typeof inv.items === 'string' ? JSON.parse(inv.items) : inv.items;
      if (parsedItems && parsedItems.length > 0) {
        service = (parsedItems[0].itemName || parsedItems[0].description?.split(':')[0] || 'Service').toLowerCase();
      }
    } catch (e) {}

    return String(inv.invoice_num).includes(searchLower) ||
           billTo.includes(searchLower) ||
           service.includes(searchLower) ||
           String(inv.invoice_date).includes(searchLower) ||
           String((inv.total || 0).toFixed(2)).includes(searchLower);
  });

  const handleExportToExcel = async () => {
    const XLSX = await import('xlsx');
    
    const exportData = filteredInvoices.map((inv) => {
      let billTo = 'Unknown';
      try {
        const parsed = JSON.parse(inv.bill_to_id);
        billTo = parsed.name || inv.bill_to_id || '';
      } catch (e) { billTo = inv.bill_to_id; }
      
      let parsedItems = [];
      try {
        parsedItems = typeof inv.items === 'string' ? JSON.parse(inv.items) : inv.items;
      } catch (e) {
        parsedItems = inv.items;
      }

      let serviceAmt = 0;
      let supportAmt = 0;

      parsedItems.forEach(item => {
        const amt = parseFloat(item.amount?.toString().replace(/[^0-9.-]/g, '')) || 0;
        const itemName = item.itemName || (item.description ? item.description.split(':')[0].trim() : '');
        const nameLower = itemName.toLowerCase();
        const supportAndMaintenanceItems = WORK_ITEMS.ITSupportAndMaintenance.map(wi => wi.split(':')[0].trim().toLowerCase());
        const isSupport = supportAndMaintenanceItems.includes(nameLower) || nameLower.includes('support');

        if (isSupport) supportAmt += amt;
        else serviceAmt += amt;
      });

      return {
        'Invoice #': inv.invoice_num,
        'Date': inv.invoice_date,
        'Due Date': inv.due_date,
        'Bill To': billTo,
        'Service Amount': serviceAmt.toFixed(2),
        'Support Amount': supportAmt.toFixed(2),
        'Subtotal': (inv.subtotal || 0).toFixed(2),
        'Tax': (inv.tax || 0).toFixed(2),
        'Total': (inv.total || 0).toFixed(2),
        'Status': inv.status || 'Due'
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Invoices');
    const timestamp = new Date().toISOString().replace(/T/, '_').replace(/:/g, '-').split('.')[0];
    XLSX.writeFile(workbook, `Invoices_Report_${timestamp}.xlsx`);
  };

  const totalPages = Math.max(1, Math.ceil(filteredInvoices.length / pageSize));
  const pageData = filteredInvoices.slice(pageIndex * pageSize, (pageIndex + 1) * pageSize);

  return (
    <TaxPortalLayout
      title="Invoice Generator"
      description="Create, view, manage, and print client invoices."
      portalState={portalState}
    >
      
      <style>{`
        :root {
          --primary: #0084ad;
          --primary-dark: #0b1f38;
          --text-dark: #0f172a;
          --text-muted: #64748b;
          --border-color: #e2e8f0;
          --bg-light: #f8fafc;
        }
        .invoice-box {
          background: #fff; padding: 40px; border-radius: 12px; margin: auto; box-sizing: border-box;
          box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.08), 0 8px 10px -6px rgba(0, 0, 0, 0.04);
          overflow: hidden;
        }
        .header-banner {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 24px;
          padding-bottom: 16px;
          border-bottom: 1px solid #e2e8f0;
          gap: 20px;
          margin-top: -40px;
          padding-top: 24px;
        }
        .banner-logo-block {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 6px;
          max-width: 480px;
          padding-top: 16px;
        }
        .banner-logo-block img {
          max-width: 220px;
          height: auto;
          display: block;
        }
        .company-details-banner {
          font-size: 12px;
          line-height: 1.45;
          color: #334155;
          text-align: left;
        }
        .company-details-banner strong {
          color: #0b1f38;
          font-weight: 700;
        }
        .gst-line {
          white-space: nowrap;
        }
        .banner-title-block {
          background: linear-gradient(135deg, #0b1f38 0%, #0084ad 100%);
          padding: 24px 44px 22px 36px;
          border-radius: 0 0 0 35px;
          color: #ffffff;
          box-shadow: -4px 4px 14px rgba(0, 132, 173, 0.25);
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
          margin-right: -40px;
          margin-top: -24px;
        }
        .invoice-title-banner {
          font-size: 34px;
          font-weight: 900;
          color: #ffffff;
          letter-spacing: 4px;
          margin: 0;
          text-transform: uppercase;
        }
        .info-grid { display: flex; flex-wrap: wrap; justify-content: space-between; gap: 20px; margin-bottom: 20px; }
        .company-details, .billing-details { flex: 1; min-width: 250px; font-size: 14px; line-height: 1.5; color: var(--text-dark); }
        .meta-details { flex: 1; min-width: 250px; display: flex; flex-direction: column; gap: 8px; font-size: 14px; color: var(--text-dark); align-items: flex-start; }
        @media (min-width: 768px) {
          .meta-details { align-items: flex-end; }
        }
        .meta-details label { font-weight: 600; display: flex; justify-content: space-between; align-items: center; width: 100%; max-width: 300px; }
        .meta-details input {
          font-family: inherit; border: 1px solid var(--border-color); border-radius: 4px;
          padding: 4px 8px; width: 130px; text-align: right; color: var(--text-dark);
        }
        .section-title {
          font-size: 15px; font-weight: 700; color: #0b1f38;
          border-bottom: 2px solid;
          border-image: linear-gradient(90deg, #0b1f38 0%, #0084ad 100%) 1;
          padding-bottom: 6px; margin-bottom: 12px; display: flex; justify-content: space-between; align-items: center;
          text-transform: uppercase; letter-spacing: 0.5px;
        }
        select {
          font-family: inherit; padding: 6px 12px; border: 1px solid var(--border-color);
          border-radius: 4px; background-color: #fff; width: 100%; max-width: 320px; cursor: pointer; color: var(--text-dark);
        }
        .period-container { display: flex; flex-direction: column; align-items: flex-start; gap: 6px; }
        td select { padding: 4px 8px; width: 100%; max-width: 120px; font-size: 13px; }
        .date-range { display: flex; align-items: center; gap: 6px; font-size: 13px; color: var(--text-muted); }
        .date-range input[type="date"] {
          font-family: inherit; border: 1px solid var(--border-color); border-radius: 4px;
          padding: 2px 6px; font-size: 12px; color: var(--text-dark);
        }
        .billing-address-box { margin-top: 8px; font-size: 14px; line-height: 1.5; color: var(--text-muted); }
        .print-address-view { display: none; }
        table {
          width: 100%; border-collapse: separate; border-spacing: 0; margin-top: 15px; font-size: 14px;
          border-radius: 8px; overflow: hidden; border: 1px solid #e2e8f0;
        }
        th {
          color: #ffffff; font-weight: 700;
          text-transform: uppercase; font-size: 12px; letter-spacing: 0.8px;
          padding: 12px 14px; text-align: left;
          -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important;
        }
        th:nth-child(1) { background-color: #0b1f38; }
        th:nth-child(2) { background-color: #0084ad; }
        th:nth-child(3) { background-color: #006f93; }
        th:nth-child(4) { background-color: #061527; }
        th:nth-child(5) { background-color: #061527; text-align: center; }
        th.right-align, td.right-align { text-align: right; }
        th.left-align, td.left-align { text-align: left; }
        th.center-align, td.center-align { text-align: center; }
        td { border-bottom: 1px solid var(--border-color); padding: 12px 14px; vertical-align: top; }
        tr:last-child td { border-bottom: none; }
        td:nth-child(2), td:nth-child(3) { background-color: #f8fafc; }
        textarea {
          width: 100%; border: none; font-family: inherit; font-size: 14px;
          resize: none; background: transparent; line-height: 1.4; color: var(--text-dark);
        }
        td input[type="text"] {
          width: 100%; border: none; font-family: inherit; font-size: 14px;
          text-align: right; background: transparent; color: var(--text-dark); min-width: 80px;
        }
        input:focus, textarea:focus, select:focus { outline: 2px solid #0084ad; outline-offset: -1px; border-radius: 4px; }
        .btn-container { max-width: 850px; margin: 20px auto 0 auto; display: flex; flex-wrap: wrap; gap: 10px; justify-content: flex-end; }
        .btn-primary-action {
          padding: 10px 24px; background: #0084ad; color: #fff; border: none;
          border-radius: 6px; cursor: pointer; font-size: 14px; font-weight: 600; transition: background 0.2s ease;
        }
        .btn-primary-action:hover { background: #0b1f38; }
        .add-icon {
          font-size: 14px; font-weight: 600; color: #0084ad; cursor: pointer;
          display: inline-flex; align-items: center; gap: 4px; padding: 4px 8px; border-radius: 4px; background: #e0f2fe;
        }
        .add-icon:hover { background: #bae6fd; }
        .remove-btn { color: #ef4444; cursor: pointer; font-size: 16px; user-select: none; }
        .remove-btn:hover { color: #dc2626; }
        .summary-container {
          margin-top: 20px;
          display: flex;
          justify-content: space-between;
          flex-direction: row-reverse;
          flex-wrap: wrap;
          gap: 20px;
          page-break-inside: avoid !important;
          break-inside: avoid !important;
        }
        .summary-box {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          gap: 8px;
          font-size: 14px;
          color: var(--text-dark);
          page-break-inside: avoid !important;
          break-inside: avoid !important;
        }
        .summary-row { display: flex; justify-content: space-between; width: 250px; }
        .summary-row.total {
          font-size: 18px; border-top: 2px solid var(--border-color); padding: 12px 18px; margin-top: 6px;
          background: linear-gradient(90deg, #0b1f38 0%, #0084ad 100%); color: #ffffff; border-radius: 8px; font-weight: 800;
          box-shadow: 0 4px 14px rgba(0, 132, 173, 0.25);
          -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important;
        }
        .payment-instructions {
          background: #f8fafc; padding: 20px; border-radius: 8px;
          font-size: 13px; line-height: 1.6; color: var(--text-muted);
          border: 1px solid #e2e8f0; border-left: 4px solid #0084ad;
          width: 100%; max-width: 400px;
          page-break-inside: avoid !important; break-inside: avoid !important;
          box-shadow: 0 1px 3px rgba(0,0,0,0.03);
        }
        .payment-instructions h3 { margin-top: 0; margin-bottom: 8px; color: #0b1f38; font-size: 14px; font-weight: 700; }
        .bill-to-name { font-weight: 600; color: var(--text-dark); }
        .footer-banner {
          margin-top: 35px;
          background: linear-gradient(135deg, #0b1f38 0%, #0b1f38 72%, #0084ad 72%, #0084ad 100%);
          padding: 16px 24px;
          border-radius: 6px;
          color: #ffffff;
          font-size: 14px;
          font-weight: 700;
          letter-spacing: 1.5px;
          text-transform: uppercase;
          text-align: center;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
          page-break-inside: avoid !important;
          break-inside: avoid !important;
        }
        .print-text { display: none; white-space: pre-wrap; font-family: inherit; font-size: 14px; line-height: 1.4; color: #000; }
        
        /* Mobile Responsive Styles (Screen View Only) */
        @media screen and (max-width: 767px) {
          .invoice-box {
            padding: 16px !important;
            border-radius: 8px !important;
          }
          .header-banner {
            flex-direction: column-reverse;
            align-items: stretch;
            margin-top: -16px;
            margin-left: -16px;
            margin-right: -16px;
            padding-top: 0;
            padding-bottom: 12px;
            gap: 14px;
            border-bottom: 1px solid #e2e8f0;
          }
          .banner-logo-block {
            padding-top: 0;
            padding-left: 16px;
            padding-right: 16px;
            max-width: 100%;
          }
          .banner-logo-block img {
            max-width: 170px;
          }
          .company-details-banner {
            font-size: 11px;
            line-height: 1.4;
          }
          .banner-title-block {
            margin-right: 0;
            margin-top: 0;
            padding: 12px 16px;
            border-radius: 0 0 12px 12px;
            text-align: center;
            box-shadow: 0 4px 10px rgba(0, 132, 173, 0.2);
          }
          .invoice-title-banner {
            font-size: 22px;
            letter-spacing: 2px;
          }
          .info-grid {
            flex-direction: column;
            gap: 16px;
            margin-bottom: 16px;
          }
          .meta-details {
            align-items: stretch !important;
            width: 100%;
          }
          .meta-details label {
            max-width: 100%;
          }
          .meta-details input {
            width: 140px;
          }
          .summary-container {
            flex-direction: column;
            gap: 16px;
          }
          .payment-instructions {
            max-width: 100%;
            padding: 14px;
          }
          .summary-box {
            align-items: stretch;
            width: 100%;
          }
          .summary-row {
            width: 100%;
          }
          .summary-row.total {
            width: 100%;
          }
          .footer-banner {
            margin-top: 20px;
            padding: 12px 16px;
            font-size: 12px;
            letter-spacing: 1px;
          }
          .btn-container {
            justify-content: stretch;
          }
          .btn-container button {
            flex: 1;
          }
        }
        
        @media print {
          @page { size: portrait; margin: 8mm; }
          body { background: #fff !important; margin: 0; padding: 0 !important; }
          .container, .container-fluid { max-width: 100% !important; width: 100% !important; padding: 0 !important; margin: 0 !important; }
          .py-5, .py-4 { padding-top: 0 !important; padding-bottom: 0 !important; }
          .invoice-page-container { padding: 0 !important; background: #fff !important; }
          .invoice-wrapper { width: 100% !important; background: #fff !important; margin: 0; padding: 0 !important; }
          .no-print { display: none !important; }
          .invoice-box {
            padding: 24px 30px !important;
            border: 1px solid #d1d5db !important;
            border-radius: 8px !important;
            box-shadow: none !important;
            max-width: 100% !important;
            width: 100% !important;
            margin: 0 !important;
            box-sizing: border-box !important;
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }
          .header-banner {
            padding: 18px 24px !important;
            margin-bottom: 16px !important;
          }
          .info-grid {
            margin-bottom: 12px !important;
          }
          .actions-col { display: none !important; }
          input, textarea, select { border: none !important; background: transparent !important; padding: 0 !important; outline: none !important; appearance: none !important; -webkit-appearance: none !important; }
          textarea { resize: none; overflow: hidden; }
          input::placeholder, textarea::placeholder { color: transparent !important; }
          .bill-to-name { font-size: 16px !important; font-weight: 700 !important; color: #000 !important; }
          .bill-to-email { font-size: 14px !important; color: #333 !important; }
          .no-print-textarea { display: none !important; }
          .print-text { display: block !important; }
          .print-address-view { display: block !important; }
          .Toastify { display: none !important; }
          
          .data-page-header { display: none !important; }
          .period-container select { display: none !important; }
          .date-range { display: none !important; }
          .print-period-view { display: block !important; font-size: 14px; color: #000; text-align: left; }
          table { margin-top: 10px !important; }
          td, th { padding: 8px 10px !important; font-size: 13px !important; }
          tr { page-break-inside: avoid !important; break-inside: avoid !important; }
          .section-title { margin-bottom: 5px !important; padding-bottom: 2px !important; }
          .summary-container {
            margin-top: 12px !important;
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }
          .payment-instructions {
            padding: 12px 16px !important;
            font-size: 12px !important;
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }
          .summary-box {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }
          .footer-banner {
            margin-top: 20px !important;
            padding: 12px 20px !important;
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }
        }
      `}</style>

      {viewMode === 'dashboard' && isAdmin ? (
        <div className="container-fluid px-0 no-print">
          {isAdmin && (
            <div className="card mb-4 border-0 rounded-4 shadow-sm overflow-hidden bg-white" style={{ border: '1px solid #e2e8f0', boxShadow: '0 10px 30px -5px rgba(15, 23, 42, 0.06), 0 4px 12px -2px rgba(15, 23, 42, 0.03)' }}>
              <div className="card-body p-4 p-lg-5 position-relative">
                {/* Subtle Soft Background Orbs */}
                <div style={{ position: 'absolute', top: '-40px', right: '-40px', width: '260px', height: '260px', background: 'radial-gradient(circle, rgba(34, 197, 94, 0.08) 0%, rgba(0,0,0,0) 70%)', pointerEvents: 'none' }} />
                <div style={{ position: 'absolute', bottom: '-40px', left: '-40px', width: '260px', height: '260px', background: 'radial-gradient(circle, rgba(2, 132, 199, 0.06) 0%, rgba(0,0,0,0) 70%)', pointerEvents: 'none' }} />

                <div className="row g-4 align-items-center position-relative z-1">
                  {/* Left Column: Branding & Overview */}
                  <div className="col-12 col-lg-5">

                    <h2 className="fw-bold mb-2 text-dark" style={{ fontSize: '2rem', letterSpacing: '-0.02em', color: '#0f172a' }}>
                      Executive Invoice Workspace
                    </h2>
                    <p className="text-secondary small mb-4" style={{ maxWidth: '420px', lineHeight: 1.6 }}>
                      Monitor client payments, track outstanding dues, manage tax splits, and generate CRA-compliant invoices.
                    </p>

                    <div className="d-flex align-items-center gap-4 pt-2" style={{ borderTop: '1px solid #f1f5f9', flexWrap: 'wrap' }}>
                      <div>
                        <div className="text-muted text-uppercase fw-semibold" style={{ fontSize: '10px', letterSpacing: '0.6px' }}>Total Invoices</div>
                        <div className="fs-3 fw-bold text-dark mb-0">{totalCount}</div>
                      </div>
                      <div style={{ width: '1px', height: '36px', background: '#e2e8f0' }} />
                      <div>
                        <div className="text-muted text-uppercase fw-semibold" style={{ fontSize: '10px', letterSpacing: '0.6px' }}>Due Invoices</div>
                        <div className="fs-3 fw-bold text-danger mb-0">{dueCount}</div>
                      </div>
                      <div style={{ width: '1px', height: '36px', background: '#e2e8f0' }} />
                      <div>
                        <div className="text-muted text-uppercase fw-semibold" style={{ fontSize: '10px', letterSpacing: '0.6px' }}>Collected Tax</div>
                        <div className="fs-3 fw-bold text-success mb-0">${paidTaxTotal.toFixed(2)}</div>
                      </div>
                    </div>
                  </div>

                  {/* Center Column: Radial Collection Ring */}
                  <div className="col-12 col-md-5 col-lg-3 text-center" style={{ borderLeft: '1px solid #f1f5f9' }}>
                    <div className="position-relative d-inline-flex align-items-center justify-content-center">
                      <svg width="130" height="130" viewBox="0 0 120 120">
                        <circle cx="60" cy="60" r="50" fill="none" stroke="#e2e8f0" strokeWidth="10" />
                        <circle
                          cx="60" cy="60" r="50" fill="none"
                          stroke="url(#invoiceHeroGradient)" strokeWidth="10"
                          strokeDasharray={314}
                          strokeDashoffset={314 - (314 * (totalCount > 0 ? Math.round((paidCount / totalCount) * 100) : 0)) / 100}
                          strokeLinecap="round"
                          transform="rotate(-90 60 60)"
                          style={{ transition: 'stroke-dashoffset 0.8s ease' }}
                        />
                        <defs>
                          <linearGradient id="invoiceHeroGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="#22c55e" />
                            <stop offset="100%" stopColor="#16a34a" />
                          </linearGradient>
                        </defs>
                      </svg>
                      <div className="position-absolute d-flex flex-column align-items-center">
                        <span className="fs-3 fw-bold text-dark" style={{ lineHeight: 1, color: '#0f172a' }}>{totalCount > 0 ? Math.round((paidCount / totalCount) * 100) : 0}%</span>
                        <span className="text-muted" style={{ fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Collected</span>
                      </div>
                    </div>
                    <div className="mt-2 text-secondary small fw-semibold">
                      {paidCount} of {totalCount} Invoices Paid
                    </div>
                  </div>

                  {/* Right Column: Embedded KPI White Cards */}
                  <div className="col-12 col-md-7 col-lg-4">
                    <div className="d-flex flex-column gap-2">
                      {/* Card 1: Paid */}
                      <div 
                        className="p-3 rounded-3 d-flex align-items-center justify-content-between hover-lift" 
                        onClick={() => { setStatusFilter('Paid'); setPageIndex(0); }}
                        style={{ background: '#f8fafc', border: '1px solid #e2e8f0', cursor: 'pointer' }}
                      >
                        <div className="d-flex align-items-center gap-3">
                          <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: 38, height: 38, background: '#dcfce7', color: '#15803d' }}>
                            <FaMoneyBillWave size={16} />
                          </div>
                          <div>
                            <div className="text-muted fw-semibold" style={{ fontSize: '11px' }}>Paid Invoices</div>
                            <div className="fw-bold text-dark fs-5" style={{ lineHeight: 1 }}>${paidTotal.toFixed(2)}</div>
                          </div>
                        </div>
                        <span className="badge rounded-pill fw-bold px-2.5 py-1" style={{ background: '#dcfce7', color: '#15803d', border: '1px solid #bbf7d0', fontSize: '10px' }}>Paid</span>
                      </div>

                      {/* Card 2: Due */}
                      <div 
                        className="p-3 rounded-3 d-flex align-items-center justify-content-between hover-lift" 
                        onClick={() => { setStatusFilter('Due'); setPageIndex(0); }}
                        style={{ background: '#f8fafc', border: '1px solid #e2e8f0', cursor: 'pointer' }}
                      >
                        <div className="d-flex align-items-center gap-3">
                          <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: 38, height: 38, background: '#fef3c7', color: '#d97706' }}>
                            <FaClock size={16} />
                          </div>
                          <div>
                            <div className="text-muted fw-semibold" style={{ fontSize: '11px' }}>Due Invoices</div>
                            <div className="fw-bold text-dark fs-5" style={{ lineHeight: 1 }}>${dueTotal.toFixed(2)}</div>
                          </div>
                        </div>
                        <span className="badge rounded-pill fw-bold px-2.5 py-1" style={{ background: '#fef3c7', color: '#92400e', border: '1px solid #fde68a', fontSize: '10px' }}>Due</span>
                      </div>

                      {/* Card 3: Services vs Support Split */}
                      <div className="p-3 rounded-3 d-flex align-items-center justify-content-between" style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                        <div className="d-flex align-items-center gap-3">
                          <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: 38, height: 38, background: '#ede9fe', color: '#4338ca' }}>
                            <FaLaptopCode size={16} />
                          </div>
                          <div>
                            <div className="text-muted fw-semibold" style={{ fontSize: '11px' }}>Services Revenue</div>
                            <div className="fw-bold text-dark fs-6" style={{ lineHeight: 1 }}>${serviceTotal.toFixed(2)}</div>
                          </div>
                        </div>
                        <span className="badge rounded-pill fw-bold px-2.5 py-1" style={{ background: '#ede9fe', color: '#4338ca', border: '1px solid #ddd6fe', fontSize: '10px' }}>Services</span>
                      </div>
                      
                      {/* Card 4: Support */}
                      <div className="p-3 rounded-3 d-flex align-items-center justify-content-between" style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                        <div className="d-flex align-items-center gap-3">
                          <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: 38, height: 38, background: '#e0f2fe', color: '#0369a1' }}>
                            <FaHandshake size={16} />
                          </div>
                          <div>
                            <div className="text-muted fw-semibold" style={{ fontSize: '11px' }}>Support Revenue</div>
                            <div className="fw-bold text-dark fs-6" style={{ lineHeight: 1 }}>${supportTotal.toFixed(2)}</div>
                          </div>
                        </div>
                        <span className="badge rounded-pill fw-bold px-2.5 py-1" style={{ background: '#e0f2fe', color: '#0369a1', border: '1px solid #bae6fd', fontSize: '10px' }}>Support</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 gap-3">
            <h4 className="fw-bold mb-0 text-dark d-flex align-items-center gap-2">
              <FaFileInvoiceDollar className="text-primary" /> Invoice Manager 
              <span className="badge bg-light text-secondary border rounded-pill ms-2 fs-6 px-3">
                {filteredInvoices.length} {statusFilter !== 'All' ? statusFilter : ''} / {totalCount} Total
              </span>
            </h4>
            <div className="d-flex gap-2 flex-wrap align-items-center">
              <div className="position-relative" ref={filterRef}>
                <button
                  type="button"
                  className="btn btn-white rounded-pill border shadow-sm px-3 py-2 fw-semibold text-dark d-flex align-items-center gap-2 hover-lift"
                  onClick={() => setIsFilterOpen(prev => !prev)}
                  style={{
                    fontSize: '0.88rem',
                    backgroundColor: statusFilter === 'Due' ? '#fffbeb' : statusFilter === 'Paid' ? '#f0fdf4' : statusFilter === 'Cancelled' ? '#f8fafc' : '#ffffff',
                    borderColor: statusFilter === 'Due' ? '#fcd34d' : statusFilter === 'Paid' ? '#86efac' : '#e2e8f0'
                  }}
                  aria-expanded={isFilterOpen}
                  aria-label="Filter by invoice payment status"
                >
                  <FaFilter className="text-muted" size={11} />
                  <span>{statusFilter === 'Due' ? 'Due Only' : statusFilter === 'Paid' ? 'Paid Only' : statusFilter === 'Cancelled' ? 'Cancelled' : 'All Statuses'}</span>
                  <span className={`badge rounded-pill ms-1 ${statusFilter === 'Due' ? 'bg-warning-subtle text-warning-emphasis' : statusFilter === 'Paid' ? 'bg-success-subtle text-success' : statusFilter === 'Cancelled' ? 'bg-secondary-subtle text-secondary' : 'bg-primary-subtle text-primary'}`} style={{ fontSize: '10px' }}>
                    {statusFilter === 'Due' ? dueCount : statusFilter === 'Paid' ? paidCount : statusFilter === 'Cancelled' ? cancelledCount : totalCount}
                  </span>
                </button>

                {isFilterOpen && (
                  <div 
                    className="position-absolute start-0 mt-2 shadow-lg bg-white border rounded-4 py-2"
                    style={{ minWidth: '175px', top: '100%', zIndex: 1050, boxShadow: '0 10px 25px -5px rgba(0,0,0,0.12), 0 8px 10px -6px rgba(0,0,0,0.08)' }}
                  >
                    <button
                      type="button"
                      className={`dropdown-item px-3 py-2 fw-semibold d-flex align-items-center justify-content-between gap-2 border-0 bg-transparent ${statusFilter === 'Due' ? 'bg-warning-subtle text-dark font-bold' : 'text-dark'}`}
                      onClick={() => { setStatusFilter('Due'); setPageIndex(0); setIsFilterOpen(false); }}
                      style={{ fontSize: '0.86rem', cursor: 'pointer' }}
                    >
                      <span className="d-flex align-items-center gap-2">
                        <FaClock className="text-warning" size={13} /> Due Only
                      </span>
                      <span className="badge rounded-pill bg-warning-subtle text-warning-emphasis ms-auto" style={{ fontSize: '10px' }}>{dueCount}</span>
                    </button>

                    <button
                      type="button"
                      className={`dropdown-item px-3 py-2 fw-semibold d-flex align-items-center justify-content-between gap-2 border-0 bg-transparent ${statusFilter === 'Paid' ? 'bg-success-subtle text-dark font-bold' : 'text-dark'}`}
                      onClick={() => { setStatusFilter('Paid'); setPageIndex(0); setIsFilterOpen(false); }}
                      style={{ fontSize: '0.86rem', cursor: 'pointer' }}
                    >
                      <span className="d-flex align-items-center gap-2">
                        <FaCheckCircle className="text-success" size={13} /> Paid Only
                      </span>
                      <span className="badge rounded-pill bg-success-subtle text-success ms-auto" style={{ fontSize: '10px' }}>{paidCount}</span>
                    </button>

                    <button
                      type="button"
                      className={`dropdown-item px-3 py-2 fw-semibold d-flex align-items-center justify-content-between gap-2 border-0 bg-transparent ${statusFilter === 'Cancelled' ? 'bg-secondary-subtle text-dark font-bold' : 'text-dark'}`}
                      onClick={() => { setStatusFilter('Cancelled'); setPageIndex(0); setIsFilterOpen(false); }}
                      style={{ fontSize: '0.86rem', cursor: 'pointer' }}
                    >
                      <span className="d-flex align-items-center gap-2">
                        <FaBan className="text-secondary" size={13} /> Cancelled
                      </span>
                      <span className="badge rounded-pill bg-secondary-subtle text-secondary ms-auto" style={{ fontSize: '10px' }}>{cancelledCount}</span>
                    </button>

                    <div className="dropdown-divider my-1"></div>

                    <button
                      type="button"
                      className={`dropdown-item px-3 py-2 fw-semibold d-flex align-items-center justify-content-between gap-2 border-0 bg-transparent ${statusFilter === 'All' ? 'bg-primary-subtle text-dark font-bold' : 'text-dark'}`}
                      onClick={() => { setStatusFilter('All'); setPageIndex(0); setIsFilterOpen(false); }}
                      style={{ fontSize: '0.86rem', cursor: 'pointer' }}
                    >
                      <span className="d-flex align-items-center gap-2">
                        <FaList className="text-primary" size={13} /> All Statuses
                      </span>
                      <span className="badge rounded-pill bg-primary-subtle text-primary ms-auto" style={{ fontSize: '10px' }}>{totalCount}</span>
                    </button>
                  </div>
                )}
              </div>
              <div className="position-relative">
                <FaFilter className="text-muted position-absolute top-50 start-0 translate-middle-y ms-3" size={11} />
                <select
                  className="form-select rounded-pill border shadow-sm ps-5 py-2 fw-semibold text-dark"
                  value={yearFilter}
                  onChange={(e) => { setYearFilter(e.target.value); setPageIndex(0); }}
                  style={{ fontSize: '0.88rem', cursor: 'pointer' }}
                  aria-label="Filter by year"
                >
                  <option value="">All Years</option>
                  {yearOptions.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
              <div className="position-relative">
                <FaSearch className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" size={14} />
                <input
                  type="text"
                  className="form-control rounded-pill border shadow-sm ps-5 pe-4"
                  placeholder="Search invoices..."
                  value={searchText}
                  onChange={(e) => { setSearchText(e.target.value); setPageIndex(0); }}
                  style={{ width: '220px', fontSize: '0.9rem', borderColor: searchText ? '#0284c7' : '#e2e8f0' }}
                />
                {searchText && (
                  <button
                    className="btn border-0 bg-transparent text-muted position-absolute top-50 end-0 translate-middle-y me-2 p-0"
                    onClick={() => { setSearchText(''); setPageIndex(0); }}
                    title="Clear search"
                    type="button"
                  >
                    <FaTimes size={12} />
                  </button>
                )}
              </div>
              <button 
                className="btn btn-light rounded-pill shadow-sm border fw-bold px-3 d-flex align-items-center gap-2 hover-lift"
                onClick={handleExportToExcel}
                disabled={filteredInvoices.length === 0}
              >
                <FaFileExcel className="text-success" /> <span className="d-none d-sm-inline">Export</span>
              </button>
              <button 
                className="btn btn-primary rounded-pill shadow-sm fw-bold px-4 d-flex align-items-center gap-2 hover-lift"
                onClick={() => {
                  setInvoiceId(null);
                  setInvoiceNum('');
                  setItems([{ id: Date.now(), itemName: "", itemDesc: "", period: "yearly", startDate: "", endDate: "", amount: "", hst: true }]);
                  setBillToName('');
                  setBillToEmail('');
                  setBillToAddress('');
                  setSourceRecord(null);
                  setViewMode('form');
                }}
              >
                <FaPlus size={12} /> <span className="d-none d-sm-inline">Create Invoice</span><span className="d-inline d-sm-none">New</span>
              </button>
            </div>
          </div>


          <div className="d-block d-md-none">
            {isLoading ? (
              <div className="d-flex flex-column gap-3">
                {[1, 2, 3].map(i => (
                  <div key={i} className="card border-0 rounded-4" style={{ overflow: 'hidden' }}>
                    <div className="card-body p-3">
                      <div className="d-flex gap-2 align-items-center mb-3">
                        <span className="skeleton-cell" style={{ width: 36, height: 36, borderRadius: '50%' }} />
                        <div>
                          <span className="skeleton-cell d-block mb-1" style={{ width: 120, height: 13 }} />
                          <span className="skeleton-cell d-block" style={{ width: 80, height: 11 }} />
                        </div>
                      </div>
                      <span className="skeleton-cell" style={{ width: '100%', height: 32, borderRadius: 8 }} />
                    </div>
                  </div>
                ))}
              </div>
            ) : pageData.length === 0 ? (
              <div className="card border-0 rounded-4 shadow-sm bg-white">
                <div className="card-body text-center py-5">
                  <FaInbox className="text-muted mb-3 opacity-50" size={32} />
                  <h6 className="fw-bold text-dark mb-1">No matching invoices</h6>
                  <p className="mb-0 text-muted small">Try adjusting your search criteria.</p>
                </div>
              </div>
            ) : (
              pageData.map(inv => {
                let billTo = 'Unknown';
                try {
                  const parsed = JSON.parse(inv.bill_to_id);
                  billTo = parsed.name || inv.bill_to_id || '';
                } catch (e) { billTo = inv.bill_to_id; }

                const avatar = getAvatarColor(billTo);

                return (
                  <div key={inv.id} className="card mb-3 mobile-record-card shadow-sm border-0 rounded-4">
                    <div className="card-body p-3">
                      <div className="d-flex justify-content-between align-items-start mb-3">
                        <div className="d-flex align-items-center gap-2">
                          <div className="name-avatar flex-shrink-0" style={{ backgroundColor: avatar.bg, color: avatar.text, width: 36, height: 36, fontSize: 12 }}>
                            {getInitials(billTo)}
                          </div>
                        <div>
                            <h6 className="mb-0 fw-bold text-dark text-truncate" style={{ maxWidth: '160px' }}>{billTo}</h6>
                            <div className="text-muted small mt-1">#{inv.invoice_num} • {inv.invoice_date}</div>
                          </div>
                        </div>
                        <div className="dropdown">
                          <button className="action-menu-trigger dropdown-toggle" type="button" data-bs-toggle="dropdown" aria-expanded="false" data-bs-boundary="window" aria-label="Actions" title="Actions">
                            <FaEllipsisV size={13} />
                          </button>
                          <ul className="dropdown-menu dropdown-menu-end shadow-sm border-0">
                            <li><button className="dropdown-item" onClick={() => handleViewInvoice(inv)}><FaEye className="me-2 text-primary" /> View / Edit Details</button></li>
                            <li><button className="dropdown-item" onClick={() => handleEmailInvoice(inv)}><FaEnvelope className="me-2 text-success" /> Email Invoice</button></li>
                          </ul>
                        </div>
                      </div>
                      
                      <div className="d-flex justify-content-between align-items-center p-2 bg-light rounded-3 mb-2">
                        <span className="text-muted small">Total Amount</span>
                        <span className="fw-bold text-dark fs-5">${(inv.total || 0).toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          <div className="card shadow-sm border-0 d-none d-md-block rounded-4 bg-white">
            <div className="table-responsive rounded-4">
              <table className="table table-modern table-hover align-middle mb-0" style={{ tableLayout: 'fixed' }}>
                <colgroup>
                  <col style={{ width: '9%' }} />
                  <col style={{ width: '11%' }} />
                  <col style={{ width: '19%' }} />
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '9%' }} />
                  <col style={{ width: '7%' }} />
                  <col style={{ width: '9%' }} />
                  <col style={{ width: '11%' }} />
                  <col style={{ width: '7%' }} />
                </colgroup>
                <thead>
                  <tr className="table-header-sep text-muted text-uppercase" style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.5px' }}>
                    <th className="px-3 py-3 border-0">Invoice #</th>
                    <th className="py-3 border-0">Date</th>
                    <th className="py-3 border-0">Bill To</th>
                    <th className="py-3 border-0">Services</th>
                    <th className="py-3 border-0">Support</th>
                    <th className="py-3 border-0">Subtotal</th>
                    <th className="py-3 border-0">Tax</th>
                    <th className="py-3 border-0">Total</th>
                    <th className="py-3 border-0">Status</th>
                    <th className="py-3 border-0">Action</th>
                  </tr>
                </thead>
                <tbody className="border-top-0">
                  {isLoading ? (
                    <SkeletonRows count={8} />
                  ) : pageData.length === 0 ? (
                    <tr>
                      <td colSpan="10">
                        <div className="text-center py-5">
                          <FaInbox className="text-muted mb-3 opacity-50" size={36} />
                          <h5 className="fw-bold text-dark mb-1">No invoices found</h5>
                          <p className="mb-0 text-muted">No records match your current filters or you haven't created any invoices yet.</p>
                        </div>
                      </td>
                    </tr>
                  ) : pageData.map(inv => {
                    let billTo = 'Unknown';
                    try {
                      const parsed = JSON.parse(inv.bill_to_id);
                      billTo = parsed.name || inv.bill_to_id || '';
                    } catch (e) { billTo = inv.bill_to_id; }
                    
                    let serviceAmt = 0; let supportAmt = 0;
                    try {
                      const parsedItems = typeof inv.items === 'string' ? JSON.parse(inv.items) : inv.items;
                      if (parsedItems && Array.isArray(parsedItems)) {
                        parsedItems.forEach(item => {
                          const amt = parseFloat(item.amount?.toString().replace(/[^0-9.-]/g, '')) || 0;
                          const itemName = item.itemName || (item.description ? item.description.split(':')[0].trim() : '');
                          const nameLower = itemName.toLowerCase();
                          const supportAndMaintenanceItems = WORK_ITEMS.ITSupportAndMaintenance.map(wi => wi.split(':')[0].trim().toLowerCase());
                          const isSupport = supportAndMaintenanceItems.includes(nameLower) || nameLower.includes('support');

                          if (isSupport) supportAmt += amt;
                          else serviceAmt += amt;
                        });
                      }
                    } catch (e) {}

                    return (
                      <tr key={inv.id}>
                        <td className="px-3 fw-bold text-dark">#{inv.invoice_num}</td>
                        <td className="text-nowrap text-muted small">{inv.invoice_date}</td>
                        <td className="fw-medium text-dark text-truncate">
                          <div className="d-flex align-items-center gap-2">
                            <ClientAvatar name={billTo} />
                            <span className="text-truncate" title={billTo}>{billTo}</span>
                          </div>
                        </td>
                        <td className="text-muted">${serviceAmt.toFixed(2)}</td>
                        <td className="text-muted">${supportAmt.toFixed(2)}</td>
                        <td className="text-muted">${(inv.subtotal || 0).toFixed(2)}</td>
                        <td className="text-muted">${(inv.tax || 0).toFixed(2)}</td>
                        <td className="fw-bold text-dark">${(inv.total || 0).toFixed(2)}</td>
                        <td>
                          <select 
                            className={`form-select form-select-sm fw-bold border rounded-pill shadow-sm status-pill ${inv.status === 'Paid' ? 'text-success border-success bg-success-subtle' : inv.status === 'Cancelled' ? 'text-secondary border-secondary bg-light' : 'text-warning border-warning bg-warning-subtle'}`}
                            value={inv.status || 'Due'}
                            onChange={(e) => handleStatusChange(inv, e.target.value)}
                            style={{ cursor: 'pointer' }}
                          >
                            <option value="Due" className="text-dark">Due</option>
                            <option value="Paid" className="text-dark">Paid</option>
                            <option value="Cancelled" className="text-dark">Cancelled</option>
                          </select>
                        </td>
                        <td>
                          <div className="dropdown">
                            <button className="action-menu-trigger dropdown-toggle" type="button" data-bs-toggle="dropdown" aria-expanded="false" data-bs-boundary="window" aria-label="Actions" title="Actions">
                              <FaEllipsisV size={13} />
                            </button>
                            <ul className="dropdown-menu dropdown-menu-end shadow-sm border-0">
                              <li>
                                <button className="dropdown-item py-2" onClick={() => handleViewInvoice(inv)}>
                                  <FaEye className="me-2 text-primary" /> View / Edit Details
                                </button>
                              </li>
                              <li>
                                <button className="dropdown-item py-2" onClick={() => handleEmailInvoice(inv)}>
                                  <FaEnvelope className="me-2 text-success" /> Email Invoice
                                </button>
                              </li>
                            </ul>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
            
          {/* Pagination (visible on both mobile and desktop) */}
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-3 mt-4 px-1">
            <div className="d-flex align-items-center gap-2">
              <span className="rounded-pill px-3 py-1 fw-semibold" style={{ background: '#f8fafc', color: '#475569', fontSize: '0.78rem', border: '1px solid #e2e8f0' }}>
                {filteredInvoices.length === 0 ? '0' : `${pageIndex * pageSize + 1}–${Math.min(filteredInvoices.length, (pageIndex + 1) * pageSize)}`}
                <span className="text-muted fw-normal"> of </span>
                {filteredInvoices.length}
              </span>
              <span className="text-muted" style={{ fontSize: '0.78rem' }}>invoices</span>
            </div>
            
            <div className="d-flex align-items-center gap-1">
              <button className="page-pill" disabled={pageIndex === 0} onClick={() => setPageIndex(p => Math.max(p - 1, 0))} aria-label="Previous page">
                <FaChevronLeft size={10} /> Prev
              </button>
              {(() => {
                if (totalPages <= 1) return null;
                const _items = [];
                const addPage = (i) => _items.push(
                  <button key={i} className={`page-pill${i === pageIndex ? ' active' : ''}`} onClick={() => setPageIndex(i)} aria-label={`Page ${i + 1}`} aria-current={i === pageIndex ? 'page' : undefined}>
                    {i + 1}
                  </button>
                );
                const addDots = (key) => _items.push(<span key={key} className="page-dots">···</span>);
                
                if (totalPages <= 7) {
                  for (let i = 0; i < totalPages; i++) addPage(i);
                } else {
                  addPage(0);
                  if (pageIndex > 2) addDots('l');
                  for (let i = Math.max(1, pageIndex - 1); i <= Math.min(totalPages - 2, pageIndex + 1); i++) addPage(i);
                  if (pageIndex < totalPages - 3) addDots('r');
                  addPage(totalPages - 1);
                }
                return _items;
              })()}
              <button className="page-pill" disabled={pageIndex >= totalPages - 1} onClick={() => setPageIndex(p => Math.min(p + 1, totalPages - 1))} aria-label="Next page">
                Next <FaChevronRight size={10} />
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="invoice-wrapper">
          {isAdmin && (
            <div className="no-print mb-4" style={{ maxWidth: '850px', margin: '0 20px' }}>
               <button className="btn btn-light rounded-pill shadow-sm border fw-bold px-3 py-2 d-inline-flex align-items-center hover-lift" onClick={() => { setViewMode('dashboard'); }}>
                 <FaArrowLeft className="me-2" /> Back to Dashboard
               </button>
            </div>
          )}

        <div className="invoice-box">
          <div className="header-banner">
            <div className="banner-logo-block">
              <img className="logo" src={Logo} alt="Magizhini Technology Services Inc. Logo" />
              <div className="company-details-banner">
                <strong style={{ fontSize: "14px" }}>Magizhini Technology Services Inc.</strong><br />
                44 Abelard Ave, Brampton, ON L6Y2K8, Canada<br />
                <strong>Contact:</strong> magizhinitech@gmail.com | (647) 687-4226<br />
                <span className="gst-line"><strong>GST/HST:</strong> 710351909 RT0001</span>
              </div>
            </div>
            <div className="banner-title-block">
              <h1 className="invoice-title-banner">INVOICE</h1>
            </div>
          </div>

          <div className="info-grid" style={{ marginBottom: "20px" }}>
            <div className="billing-details">
              <div className="section-title">Invoice To:</div>
              <div className="billing-address-box mt-2" style={{ maxWidth: '350px' }}>
                <div className="no-print d-flex flex-column gap-1">
                  <input 
                    type="text" 
                    className="form-control form-control-sm p-1 bill-to-name" 
                    placeholder="Client / Company Name" 
                    value={billToName} 
                    onChange={e => setBillToName(e.target.value)} 
                  />
                  <AddressAutocomplete 
                    className="form-control form-control-sm p-1" 
                    placeholder="Billing Address"
                    value={billToAddress}
                    onChange={(e) => {
                      setBillToAddress(typeof e === 'string' ? e : e.target.value);
                    }}
                    onPlaceSelected={(place) => {
                      if (place && place.formatted_address) {
                        setBillToAddress(place.formatted_address);
                      }
                    }}
                  />
                  <input 
                    type="email" 
                    className="form-control form-control-sm p-1 bill-to-email" 
                    placeholder="Email Address" 
                    value={billToEmail} 
                    onChange={e => setBillToEmail(e.target.value)} 
                  />
                </div>
                <div className="print-address-view">
                  <div style={{ fontSize: '16px', fontWeight: '700', color: '#000' }}>{billToName}</div>
                  {billToAddress && <div style={{ whiteSpace: 'pre-wrap', fontSize: '14px', color: '#000', lineHeight: '1.2' }}>{billToAddress}</div>}
                  {billToEmail && <div style={{ fontSize: '14px', color: '#333' }}>{billToEmail}</div>}
                </div>
              </div>
            </div>
            <div className="meta-details">
              <label>Invoice #: <input type="text" value={invoiceNum} onChange={e => setInvoiceNum(e.target.value)} style={{ backgroundColor: 'transparent', textAlign: 'left' }} /></label>
              <label>Invoice Date: <input type="date" value={invoiceDate} onChange={e => setInvoiceDate(e.target.value)} style={{ backgroundColor: 'transparent', textAlign: 'left' }} /></label>
              <label>Due Date: <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} style={{ backgroundColor: 'transparent', textAlign: 'left' }} /></label>
            </div>
          </div>

          <div style={{ marginTop: "30px" }}>
            <div className="section-title">
              <span>Description of Services</span>
              <span className="add-icon no-print" onClick={handleAddRow}>＋ Add Item</span>
            </div>
            {/* DESKTOP TABLE - HIDDEN ON MOBILE, USED FOR PRINT */}
            <div className="table-responsive d-none d-md-block d-print-block">
            <table>
              <thead>
                <tr>
                  <th style={{ width: "40%" }}>Work Item</th>
                  <th className="left-align" style={{ width: "30%" }}>Service Period & Dates</th>
                  <th className="right-align" style={{ width: "20%", whiteSpace: "nowrap" }}>Amount (CAD)</th>
                  <th className="center-align no-print actions-col" style={{ width: "5%" }}>HST</th>
                  <th className="center-align no-print actions-col" style={{ width: "10%" }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {items.map(item => (
                  <tr key={item.id}>
                    <td>
                      <div className="no-print mb-2">
                        <select 
                          className="form-select form-select-sm"
                          onChange={(e) => {
                            if (e.target.value) {
                              let workItem = e.target.value;
                              let period = 'onetime'; // Default period
                              if (workItem.toLowerCase().includes('(monthly)')) {
                                period = 'monthly';
                                workItem = workItem.replace(/\(monthly\)/i, '').trim();
                              } else if (workItem.toLowerCase().includes('(yearly)')) {
                                period = 'yearly';
                                workItem = workItem.replace(/\(yearly\)/i, '').trim();
                              }
                              handleItemChange(item.id, 'period', period);
                              const parts = workItem.split(':');
                              handleItemChange(item.id, 'itemName', parts[0].trim());
                              handleItemChange(item.id, 'itemDesc', parts.slice(1).join(':').trim());
                              e.target.value = '';
                            }
                          }}
                          value=""
                        >
                          <option value="" disabled>-- Quick fill service description --</option>
                          {Object.entries(WORK_ITEMS).map(([category, items]) => (
                            <optgroup key={category} label={category.replace(/([A-Z])/g, ' $1').trim()}>
                              {items.map(wi => <option key={wi} value={wi}>{wi.split(':')[0]}</option>)}
                            </optgroup>
                          ))}
                        </select>
                      </div>
                      <div className="no-print-textarea d-flex flex-column gap-1">
                        <input 
                          type="text" 
                          className="fw-bold" 
                          value={item.itemName || ''} 
                          onChange={e => handleItemChange(item.id, 'itemName', e.target.value)} 
                          placeholder="Item / Service Name" 
                          style={{ width: '100%', border: 'none', background: 'transparent', outline: 'none', fontSize: '14px', color: 'var(--text-dark)', textAlign: 'left' }}
                        />
                        <textarea 
                          className="text-muted" 
                          rows="2" 
                          value={item.itemDesc || ''} 
                          onInput={handleTextareaInput} 
                          onChange={e => handleItemChange(item.id, 'itemDesc', e.target.value)} 
                          placeholder="Detailed description..." 
                          style={{ width: '100%', border: 'none', background: 'transparent', outline: 'none', fontSize: '12px', resize: 'none', overflow: 'hidden', lineHeight: '1.4' }}
                        />
                      </div>
                      <div className="print-text">
                        <div style={{ fontWeight: '600', fontSize: '14px', color: '#000', marginBottom: '2px' }}>{item.itemName}</div>
                        {item.itemDesc && <div style={{ fontSize: '12px', color: '#555', lineHeight: '1.4' }}>{item.itemDesc}</div>}
                      </div>
                    </td>
                    <td className="left-align">
                      <div className="period-container">
                        <select value={item.period} onChange={e => handleItemChange(item.id, 'period', e.target.value)}>
                          <option value="lifetime">Lifetime</option>
                          <option value="monthly">Monthly</option>
                          <option value="yearly">Yearly</option>
                          <option value="onetime">Onetime</option>
                        </select>
                        <div className="date-range">
                          <input type="date" value={item.startDate} onChange={e => handleItemChange(item.id, 'startDate', e.target.value)} />
                          <span className="date-separator">to</span>
                          <input type="date" value={item.endDate} onChange={e => handleItemChange(item.id, 'endDate', e.target.value)} />
                        </div>
                        <div className="print-period-view" style={{ display: 'none' }}>
                          <span style={{ textTransform: 'capitalize', display: 'block', fontWeight: '500', marginBottom: '4px' }}>{item.period}</span>
                          {item.startDate && item.endDate && (
                            <span style={{ fontSize: '13px', color: '#333' }}>{item.startDate} to {item.endDate}</span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="right-align">
                      <input type="text" value={item.amount} onChange={e => handleItemChange(item.id, 'amount', e.target.value)} placeholder="0.00" />
                    </td>
                    <td className="center-align no-print actions-col">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={item.hst}
                        onChange={e => handleItemChange(item.id, 'hst', e.target.checked)}
                        style={{ transform: 'scale(1.2)' }}
                      />
                    </td>
                    <td className="center-align remove-btn no-print actions-col" onClick={() => handleRemoveRow(item.id)}>🗑️</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>

          {/* MOBILE CARDS - HIDDEN ON DESKTOP & PRINT */}
          <div className="d-md-none no-print">
            {items.map((item, index) => (
              <div key={item.id} className="card mb-3 border-light shadow-sm">
                <div className="card-header bg-light py-2 px-3 border-bottom-0 d-flex justify-content-between align-items-center">
                  <span className="fw-bold text-dark small">Item #{index + 1}</span>
                  <button className="btn btn-sm btn-outline-danger p-0" style={{width: 26, height: 26}} onClick={() => handleRemoveRow(item.id)} disabled={items.length <= 1}>
                    <FaTimes />
                  </button>
                </div>
                <div className="card-body p-3 d-flex flex-column gap-3">
                  {/* Quick Fill */}
                  <select
                    className="form-select form-select-sm"
                    onChange={(e) => {
                      if (e.target.value) {
                        let workItem = e.target.value;
                        let period = 'onetime';
                        if (workItem.toLowerCase().includes('(monthly)')) {
                          period = 'monthly';
                          workItem = workItem.replace(/\(monthly\)/i, '').trim();
                        } else if (workItem.toLowerCase().includes('(yearly)')) {
                          period = 'yearly';
                          workItem = workItem.replace(/\(yearly\)/i, '').trim();
                        }
                        handleItemChange(item.id, 'period', period);
                        const parts = workItem.split(':');
                        handleItemChange(item.id, 'itemName', parts[0].trim());
                        handleItemChange(item.id, 'itemDesc', parts.slice(1).join(':').trim());
                        e.target.value = '';
                      }
                    }}
                    value=""
                  >
                    <option value="" disabled>-- Quick fill service --</option>
                    {Object.entries(WORK_ITEMS).map(([category, items]) => (
                      <optgroup key={category} label={category.replace(/([A-Z])/g, ' $1').trim()}>
                        {items.map(wi => <option key={wi} value={wi}>{wi.split(':')[0]}</option>)}
                      </optgroup>
                    ))}
                  </select>

                  {/* Item Name & Desc */}
                  <input type="text" className="form-control fw-bold" value={item.itemName || ''} onChange={e => handleItemChange(item.id, 'itemName', e.target.value)} placeholder="Item / Service Name" />
                  <textarea className="form-control text-muted" rows="2" value={item.itemDesc || ''} onInput={handleTextareaInput} onChange={e => handleItemChange(item.id, 'itemDesc', e.target.value)} placeholder="Detailed description..." style={{fontSize: '0.9rem'}} />

                  {/* Period & Dates */}
                  <div className="d-flex flex-column gap-2 p-2 bg-light rounded">
                    <label className="small fw-bold text-muted">Service Period</label>
                    <select className="form-select form-select-sm" value={item.period} onChange={e => handleItemChange(item.id, 'period', e.target.value)}>
                      <option value="lifetime">Lifetime</option>
                      <option value="monthly">Monthly</option>
                      <option value="yearly">Yearly</option>
                      <option value="onetime">Onetime</option>
                    </select>
                    <div className="d-flex align-items-center gap-2 w-100">
                      <input className="form-control form-control-sm" type="date" value={item.startDate} onChange={e => handleItemChange(item.id, 'startDate', e.target.value)} />
                      <span className="text-muted small">to</span>
                      <input className="form-control form-control-sm" type="date" value={item.endDate} onChange={e => handleItemChange(item.id, 'endDate', e.target.value)} />
                    </div>
                  </div>

                  {/* Amount & HST */}
                  <div className="d-flex align-items-center gap-3">
                    <div className="input-group">
                      <span className="input-group-text">$</span>
                      <input type="text" className="form-control" value={item.amount} onChange={e => handleItemChange(item.id, 'amount', e.target.value)} placeholder="0.00" />
                    </div>
                    <div className="form-check form-switch">
                      <input className="form-check-input" type="checkbox" role="switch" id={`hst-check-${item.id}`} checked={item.hst} onChange={e => handleItemChange(item.id, 'hst', e.target.checked)} />
                      <label className="form-check-label small" htmlFor={`hst-check-${item.id}`}>HST</label>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="summary-container" style={{ display: 'flex', marginTop: '10px', justifyContent: 'space-between', flexDirection: 'row-reverse', flexWrap: 'wrap', gap: '20px' }}>
            <div className="summary-box">
              <div className="summary-row">
                <span>Subtotal:</span><span>${subtotal.toFixed(2)}</span>
              </div>
              <div className="summary-row">
                <span>GST/HST (13%):</span><span>${tax.toFixed(2)}</span>
              </div>
              <div className="summary-row total">
                <strong>Total Due:</strong><strong>${total.toFixed(2)}</strong>
              </div>
            </div>

            <div className="payment-instructions">
              <h3>Payment Instructions</h3>
              Please make the payment via Interac e-Transfer to:<br />
              <strong>Account Name:</strong> Magizhini Technology Services Inc.<br />
              <strong>Interac e-Transfer:</strong> magizhinitech@gmail.com<br />
            </div>
          </div>
          
          <div className="footer-banner">
            Thank You For Your Business
          </div>
        </div>

        <div className="btn-container no-print d-flex justify-content-end mt-4 mb-4" style={{ maxWidth: '850px', margin: '0 auto' }}>
          <div className="d-flex gap-2 w-100 flex-column flex-sm-row justify-content-sm-end">
            <button type="button" className="btn btn-outline-primary fw-bold px-4 py-2 rounded-pill shadow-sm hover-lift" onClick={() => setShouldTriggerPrint(true)}>
              <FaPrint className="me-2" /> ! Only Print
            </button>
            <button type="button" className="btn btn-outline-primary fw-bold px-4 py-2 rounded-pill shadow-sm hover-lift" onClick={() => handleSaveInvoice(true, false)}>
              <FaPrint className="me-2" /> Save & Print
            </button>
            <button type="button" className="btn btn-primary fw-bold px-4 py-2 rounded-pill shadow-sm hover-lift" onClick={() => handleSaveInvoice(true, true)} style={{ backgroundColor: 'var(--primary)', borderColor: 'var(--primary)' }}>
              <FaEnvelope className="me-2" /> Save & Email
            </button>
          </div>
        </div>
      </div>
      )}
    </TaxPortalLayout>
  );
};

export default InvoicePage;